import React, {useEffect, useState} from 'react';
import {Button, Flex, Form, Input, List, Modal, Spin, Typography} from "antd";
import {useNavigate} from "react-router";
import { ReactComponent as CrossIcon } from '../../../../assets/images/global-icons/cross_ic.svg'
import { ReactComponent as ArrowIcon } from '../../../../assets/images/global-icons/arrow_ic.svg';
import {
  useCreateCompanyMutation,
  useSearchCompanyMutation,
  useSendJoinRequestMutation,
  useSendVerificationCompanyMailMutation,
  useValidateCompanyByDomainMutation
} from "../../../../redux/API/companySlice";
import {useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";
import { ReactComponent as CameraSvg } from '../../../../assets/images/global-icons/camera_ic.svg';
import './ChangeCompanyModal.scss';
import { googleMapsService } from '../../../../services/googleMaps';
import CustomButton from "../../../UI/CustomButton/CustomButton";

const { Text } = Typography;

interface CompanySearchModalProps {
  visible: boolean;
  onClose: () => void;
  userId: string;
  refetch: Function;
  userStatus?: string;
  userCompanyId?: string;
}


const ChangeCompanyModal:React.FC<CompanySearchModalProps> = (
  {visible, onClose, userId, refetch, userStatus, userCompanyId}
) => {
  const { isAuth } = useSelector((state: RootState) => state.isAuth);
  const [companies, setCompanies] = useState<any[]>([])
  const [companyName, setCompanyName] = useState('')
  const [isCompanyListOpen, setIsCompanyListOpen] = useState(false);
  const [changeCompanyStage, setChangeCompanyStage] = useState(1);
  const [company, setCompany] = useState<any>();
  const [emails, setEmails] = useState<any[]>();
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [searchCompany, {data, isLoading}] = useSearchCompanyMutation();
  const [createCompany] = useCreateCompanyMutation();
  const [sendJoinRequest] = useSendJoinRequestMutation();
  const [sendVerificationCompanyMail] = useSendVerificationCompanyMailMutation();
  const [validateCompany] = useValidateCompanyByDomainMutation();
  const navigate = useNavigate();

  const handleCompanyClick = async (placeId: string, _companyName: string) => {
    try {
      setChangeCompanyStage(2);
      await searchCompany({ placeId }).unwrap();
    } catch (error) {
      console.error(error);
    }
  }

  const handleCompanyEmailClick = async (company: any) => {
    try {
      if(!company.id) {
        const createdCompany = await createCompany({
          body: {
            companyName: company.companyName,
            address: company.companyAddress,
            phone: '+1-555-123-4567',
            website: company.link,
            dunsNumber: '123456789',
            placeId: company.placeId,
            email: company.companyEmail,
            link: `https://linkedin.com/company/${company.companyName}`,
            pictures: company.imageUrl,
            isValidate: false,
            description: ''
          },
          accessToken: isAuth
        }).unwrap();

        await sendJoinRequest({
          body: {
            userId: userId,
            companyId: createdCompany?.data.id
          },
          accessToken: isAuth
        }).unwrap()

        await sendVerificationCompanyMail({
          name: company.companyName,
          email: company.companyEmail
        }).unwrap()

        setChangeCompanyStage(1);
        setCompanyName('');
        await refetch();
      } else {

        await sendJoinRequest({
          body: {
            userId: userId,
            companyId: company.id
          },
          accessToken: isAuth
        }).unwrap();

        setChangeCompanyStage(1);
        setCompanyName('');
        await refetch();
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (data) {
      setCompany(data.data.company);
      setEmails(data.data.emails);
    }
  }, [data]);

  useEffect(() => {
    if (!companyName.trim()) {
      setCompanies([]);
      return;
    }

    googleMapsService.textSearch(companyName)
      .then((results) => {
        setCompanies(results);
      })
      .catch(() => {
        setCompanies([]);
      });
  }, [companyName]);

  const isRejectedByCompany = !!(company?.id && userCompanyId && company.id === userCompanyId && userStatus === 'rejected');

  const handleContactSupport = () => {
    const to = 'support@krankque.com';
    const subject = 'Request to Add My Company';
    const body = `Hello,\nI couldn't find my company ${company?.companyName} in the list during registration. Could you please assist me with adding it? Let me know what steps I need to take.\nLooking forward to your response.`;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleContinueWithEmail = async () => {
    if (!contactEmail.trim()) return;
    setContactEmailError('');
    try {
      const validateResponse = await validateCompany({ email: contactEmail }).unwrap();
      const { isEmailValid, isDomainExist } = validateResponse.data;

      if (isEmailValid && isDomainExist) {
        await handleCompanyEmailClick({
          companyName: company.companyName,
          companyEmail: contactEmail,
          companyAddress: company.address,
          imageUrl: company.picture,
          placeId: company.placeId,
          link: company.link,
          emailName: '',
          address: company.address,
          id: company.id,
          picture: company.picture
        });
        setIsContactUsModalOpen(false);
        setContactEmail('');
      } else {
        if (!isEmailValid) {
          setContactEmailError('Email is not valid');
        } else if (!isDomainExist) {
          setContactEmailError('Email domain does not exist');
        }
      }
    } catch (error) {
      setContactEmailError('Server error. Please try again later.');
    }
  };

  const handleClose = () => {
    setCompanyName('');
    setChangeCompanyStage(1);
    setIsContactUsModalOpen(false);
    setContactEmail('');
    setContactEmailError('');
    onClose();
  }

  return (
    <>
    <Modal open={visible} centered footer={null} closable={false} className="companySearchModal">
      <div className="companySearchModal_header">
        {
          changeCompanyStage === 2 &&
            <ArrowIcon className="companyModal_backIcon" onClick={() => setChangeCompanyStage(1)} />
        }
        <Flex justify={"center"}>
          <Text className="companySearchModal_title">Choose a company</Text>
        </Flex>
        <CrossIcon className="companySearchModal_closeIcon" onClick={handleClose}/>
      </div>
      <Flex vertical gap={'64px'} className="companySearchModal_content">
        {
          changeCompanyStage === 1 ?
            <>
              <Flex vertical>
                <Text className="companySearchModal_subtitle">Enter your company name</Text>
                <Text className="companySearchModal_description">Please make sure to enter the full name of the company</Text>
              </Flex>
              <Flex vertical gap={'12px'} align={"start"}>
                <span className={'companySearchModal_input__label'}>Company name</span>
                <Input
                  placeholder="Choose a company"
                  className="companySearchModal_input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  onFocus={() => setIsCompanyListOpen(true)}
                />

                {isCompanyListOpen && companyName.trim() && (
                  <div className="changeCompany-search__results result__modal">
                    <span>Result</span>
                    <List
                      dataSource={companies}
                      locale={{
                        emptyText: (
                          <Flex vertical>
                            <Flex vertical className="changeCompany-search__empty">
                              <h3>No results found.</h3>
                              <p>We couldn't find any company that match your search</p>
                            </Flex>
                            <Flex className={"companyModal__empty__button"} justify={"center"}>
                              <Button type={'link'} onClick={onClose}>Try Another Search</Button>
                            </Flex>
                          </Flex>
                        ),
                      }}
                      renderItem={(company: any) => (
                        <List.Item
                          className="changeCompany-search__item"
                          onClick={() => handleCompanyClick(company.place_id, company.name)}
                        >
                          <span>{company.name}</span>
                          <p>{company.formatted_address}</p>
                        </List.Item>
                      )}
                    />
                    {companies.length ? <p
                      className="changeCompany-search__not-found"
                      onClick={() => {
                        setCompanyName('');
                        navigate('/contact-us');
                        onClose();
                      }}
                    >
                      Can’t find your company?
                    </p> : ''}
                  </div>
                )}
              </Flex>
            </>
            :
            <>
              <div className="companyModal_content">
                {!isLoading && emails && emails.length > 0 && !isRejectedByCompany && (
                  <Flex vertical>
                    <Text className="companyModal_subtitle">Here's what we found</Text>
                    <Text className="companyModal_description">Choose your company from the list</Text>
                  </Flex>
                )}

                {isLoading || !company ? (
                  <div className="companyModal_loader">
                    <Spin size="large"/>
                  </div>
                ) : isRejectedByCompany ? (
                  <Flex vertical className="companyModal__empty" gap="64px">
                    <Flex vertical className="companyModal__empty__header">
                      <h3>No emails found</h3>
                      <p>We couldn't find any emails associated with this company</p>
                    </Flex>
                    <Flex className="companyModal__empty__button" justify="center">
                      <CustomButton text={'Contact us'} onClick={() => setIsContactUsModalOpen(true)} disable={false} />
                    </Flex>
                  </Flex>
                ) : (
                  <List
                    dataSource={emails}
                    locale={{
                      emptyText: (
                        <Flex vertical className="companyModal__empty" gap="64px">
                          <Flex vertical className="companyModal__empty__header">
                            <h3>No results found</h3>
                            <p>We couldn't find any company that matches your search</p>
                          </Flex>
                          <Flex className="companyModal__empty__button" justify="center">
                            <Button type="link" onClick={onClose}>
                              Try Another Search
                            </Button>
                          </Flex>
                        </Flex>
                      ),
                    }}
                    renderItem={(email) => (
                      <List.Item
                        className="companyModal_item"
                        onClick={() => {
                          handleCompanyEmailClick({
                            companyName: company.companyName,
                            companyEmail: email.companyEmail,
                            companyAddress: company.address,
                            imageUrl: company.picture,
                            placeId: company.placeId,
                            link: company.link,
                            emailName: email.emailName,
                            address: company.address,
                            id: company.id,
                            picture: company.picture
                          });
                          onClose();
                        }}
                      >
                        <List.Item.Meta
                         avatar={
                            company?.picture ? (
                              <img
                                className="companyModal__logo"
                                src={company.picture || "/default-company.png"}
                                alt={company.companyName}
                              />
                            ) : (
                              <div className={"companyModal__logo__CameraSvg"}>
                                <CameraSvg />
                              </div>
                            )

                          }
                          title={<Text className="companyModal_name">{email.emailName || email.companyEmail}</Text>}
                          description={<Text className="companyModal_email">{email.companyEmail}</Text>}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </div>
            </>
        }
      </Flex>
    </Modal>

    <Modal
      open={isContactUsModalOpen}
      centered
      footer={null}
      closable={false}
      className="companySearchModal"
    >
      <div className="companySearchModal_header">
        <ArrowIcon
          className="companyModal_backIcon"
          onClick={() => {
            setIsContactUsModalOpen(false);
            setContactEmail('');
            setContactEmailError('');
          }}
        />
        <Flex justify={"center"}>
          <Text className="companySearchModal_title">Choose a company</Text>
        </Flex>
        <CrossIcon className="companySearchModal_closeIcon" onClick={handleClose} />
      </div>
      <Flex vertical gap={'32px'} className="companySearchModal_content">
        <Flex vertical>
          <Text className="companySearchModal_subtitle">Enter your email address</Text>
          <Text className="companySearchModal_description">
            If your email is not listed, enter it manually and continue, or contact support for help
          </Text>
        </Flex>
        <Form layout="vertical">
          <Form.Item
            validateStatus={contactEmailError ? 'error' : ''}
            help={contactEmailError}
          >
            <Flex vertical gap={'12px'} align={"start"}>
              <span className={'companySearchModal_input__label'}>Email address</span>
              <Input
                placeholder="Your Email Address"
                className="companySearchModal_input"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  setContactEmailError('');
                }}
              />
            </Flex>
          </Form.Item>
          <Form.Item>
            <Flex vertical gap={'12px'} align={"center"} style={{marginTop: '16px'}}>
              <CustomButton
                text={'Continue with this email'}
                disable={!contactEmail.trim()}
                onClick={handleContinueWithEmail}
              />
              <Button className="companySearchModal__link" onClick={handleContactSupport} type={'link'}>
                Contact support
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    </Modal>
    </>
  );
};

export default ChangeCompanyModal;